/* eslint-disable no-case-declarations */

// @flow

import type { Currency } from '@gandi/billing';
import type { DecoratedProduct } from '@gandi/cart';
import type { ProductCategories, CommerceProductProps } from '@gandi/tracking';

const DOMAIN_PRODUCTS = ['domains', 'mailboxv2', 'domain_option'];
const VPSTemplates = ['V-R1', 'V-R4', 'V-R8', 'V-R16'];

const getTldFromFqdn = (fqdn: string): string | void => {
  if (!fqdn) {
    return undefined;
  }
  const renewTld = fqdn.split('.');

  if (renewTld.length <= 1) {
    return undefined;
  }

  renewTld.shift();
  return renewTld.join('');
};

function getCategories(item: DecoratedProduct): {
  ...ProductCategories,
  product_category5?: string,
  product_category6?: string,
} {
  let additionalCategories = {};

  switch (item.productType) {
    case 'domain':
      const { tld, corporate, tld_phase, change_owner } = item.payload;
      const { process } = item;

      const categories = {
        product_category1: 'Domain',
        product_category2: 'Domain name',
        product_category3: corporate ? 'corporate' : 'retail',
        product_category4: tld,
        product_category5: tld_phase,
      };

      if (process === 'renew') {
        // $FlowFixMe[incompatible-use]
        const isInRenewPeriod = item.renew?.in_renew_period;

        additionalCategories = {
          // $FlowFixMe[incompatible-call]
          product_category4: getTldFromFqdn(item.fqdn),
          product_category5: isInRenewPeriod
            ? 'in_renew_period'
            : 'out_renew_period',
        };
      }

      if (process === 'transfer') {
        additionalCategories = {
          product_category6: change_owner ? 'change_owner' : 'same_owner',
        };
      }

      return { ...categories, ...additionalCategories };
    case 'domain_option':
      return {
        product_category1: 'Domain',
        product_category2: item.payload.option,
      };
    case 'mailboxv2':
      return {
        product_category1: 'Domain',
        product_category2: 'Email',
        product_category3: item.payload.mailbox_type,
      };
    case 'certificate':
      return {
        product_category1: 'SSL',
        product_category2: item.payload.options?.type,
        product_category3: item.payload.options?.protect,
      };
    case 'paas':
      const isWebhosting = item.payload?.product_name === 'webhosting';
      const isCustom = item.payload.configuration === 'custom';

      if (isWebhosting) {
        additionalCategories = {
          product_category5: item.payload.domain?.fqdn
            ? 'With domain'
            : 'Without domain',
        };
      } else {
        additionalCategories = {
          product_category5: item.payload.location,
        };
      }

      return {
        product_category1: 'Hosting',
        product_category2: isWebhosting ? 'Web hosting' : 'Simple Hosting',
        product_category3:
          item.payload.extra?.application || isWebhosting
            ? 'Hosting only'
            : isCustom
              ? 'custom'
              : 'standard',
        product_category4: item.payload.size?.toString(),
        ...additionalCategories,
      };
    case 'cloud':
      const isVPS = VPSTemplates.includes(item.payload.extra?.template);
      return {
        product_category1: 'Cloud',
        product_category2: isVPS ? 'VPS' : 'IAAS',
        product_category3: item.payload.extra?.template,
        product_category4: item.payload.extra?.datacenter,
        product_category5:
          item.payload.extra?.system && item.payload.extra?.version
            ? `${item.payload.extra?.system}${item.payload.extra?.version}`
            : undefined,
      };
    case 'tmch':
      return {
        product_category1: 'Other',
        product_category2: 'TMCH',
      };
    case 'corporate':
      return {
        product_category1: 'Other',
        product_category2: 'Corporate pack',
      };
    default:
      return {};
  }
}

export function formatOrderItem(item: {
  ...DecoratedProduct,
  currency: Currency,
  ...
}): CommerceProductProps {
  const isProductDomain = DOMAIN_PRODUCTS.includes(item.productType);

  const { product_category5, product_category6, ...goodCategories } =
    getCategories(item);

  return {
    product_id: item.productType,
    // XXX: should we use `item.payload` instead to get duration and unit?
    // $FlowFixMe[incompatible-type]
    product_variant: `${item.prices[0]?.duration || 'N/A'}${item.prices[0]?.duration_unit}`,
    product: `${item.productType} - ${item.process}`,
    product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
    product_discount: item.isPromotion,
    product_pricetaxincluded: item.total?.after_taxes,
    product_pricetaxfree: item.total?.before_taxes,
    product_currency: item.currency,
    product_stock: 1,
    product_quantity: item.quantity,
    ...goodCategories,
    customObject: {
      product_category5,
      product_category6,
    },
  };
}
